/**
 * URL path configuration map.
 *
 * Verbatim port of RW-ERP-Service/src/types/constants/route.ts urlPathConfiguration.
 * Maps routeId -> { urlPath: string[] }.
 * Used by isActive() to determine if the current browser path matches a menu item.
 */
import {
  adminRouteId,
  allProductsInventoryRouteId,
  amazonCanadaRouteId,
  amazonDfRouteId,
  amazonPoRouteId,
  amazonRouteId,
  amazonSellerRouteId,
  archivedInventoryRouteId,
  arContactsRouteId,
  baldorDfRouteId,
  baldorRouteId,
  baldorWarehouseRouteId,
  bradyRouteId,
  categoryAndCollectionRouteId,
  chefsWarehouseRouteId,
  compassRouteId,
  containerRouteId,
  cwDirectRouteId,
  cwDropshipRouteId,
  cwRouteId,
  cwStagedOrdersRouteId,
  cwStockRouteId,
  distributorRouteId,
  ediDashboardRouteId,
  enrichProduct,
  europeanImportsRouteId,
  fourSeasonsRouteId,
  gfsCanadaRouteId,
  gfsRouteId,
  gfsStoresRouteId,
  gfsUsRouteId,
  ideationProduct,
  incomingInventoryRouteId,
  inStockRouteId,
  jetfreshRouteId,
  jobsRouteId,
  kpiDashboardRouteId,
  legendsRouteId,
  materialCombinationRouteId,
  northwoodRouteId,
  ordersRouteId,
  outOfStockRouteId,
  pendingCatalogsRouteId,
  poRouteId,
  produceProRouteId,
  productExportRouteId,
  salesExportRouteId,
  shipmentRouteId,
  shippingPricesRouteId,
  singerEquipmentRouteId,
  sodexoRouteId,
  sotfRouteId,
  specificationRouteId,
  stagedOrdersRouteId,
  storeFrontRouteId,
  syscoDirectRouteId,
  syscoRouteId,
  taoGroupRouteId,
  usFoodsCsRouteId,
  usFoodsDirectRouteId,
  usFoodsRouteId,
  usFoodsUsRouteId,
  vendorsRouteId,
  zoroRouteId,
} from "./routePermissions";

export interface UrlPathEntry {
  urlPath: string[];
}

export const urlPathConfiguration: Record<string, UrlPathEntry> = {
  [specificationRouteId]: {
    urlPath: [
      "/products/settings/specification",
      "/products/settings/specification?location=category",
      "/products/settings/specification?location=jobs",
    ],
  },
  [materialCombinationRouteId]: {
    urlPath: [
      "/product/material-combinations",
      "/product/material-combinations?active=working",
      "/product/material-combinations?active=approved",
      "/product/material-combinations?active=ideation",
      "/materialsubtype/ideation/combination",
      "/materialsubtype/enrich/combination",
      "/materialsubtype/enrich/combination/section/workingcopy",
      "/materialsubtype/enrich/combination/workingcopy",
      "/materialsubtype/ideation/combination/NEW_MATERIAL_COMBINATION",
      "/materialsubtype/enrich/combination/section/workingcopy/NEW_MATERIAL_WORKSHEETID",
    ],
  },
  // `ordersRouteId` doubles as a PERMISSION id (ROUTE_PERMISSIONS maps
  // `/orders` and `/orders/order` to it) and as the "All orders" menu
  // leaf's routeId for ACTIVE-STATE matching — two different concerns
  // sharing one constant. This entry only ever carried the permission
  // paths, never the leaf's actual route (`/orders/sales-orders/all`),
  // so "All orders" itself only ever showed active via the separate
  // `findExact` exact-string fallback in useActiveMenu.ts, and "Sales
  // orders" never got its parent-highlight (`isChildActive` walks THIS
  // registry) whenever "All orders" was the active leaf — the single
  // most common page in the app. `/orders` is also kept since it's a
  // real alias route for the same page (`AppRoutes.tsx`).
  [ordersRouteId]: {
    urlPath: [
      "/orders/order",
      "/orders/order/sales-orders",
      "/orders",
      "/orders/sales-orders/all",
    ],
  },
  [jetfreshRouteId]: {
    urlPath: ["/orders/staged-orders/jetfresh"],
  },
  [stagedOrdersRouteId]: {
    urlPath: ["/orders/staged-orders", "/orders/staged-orders/all"],
  },
  // Had no registry entry at all — same symptom as the Sales-orders
  // gaps above: "CW orders" itself still showed active via the
  // `findExact` fallback, but "Staged orders" never got parent-
  // highlighted while CW orders was the active leaf.
  [cwStagedOrdersRouteId]: {
    urlPath: ["/orders/staged-orders/cw-order"],
  },
  [distributorRouteId]: {
    urlPath: ["/orders/sales-orders/all", "/orders/sales-orders"],
  },
  [compassRouteId]: {
    urlPath: ["/orders/sales-orders/compass"],
  },
  [amazonRouteId]: {
    urlPath: ["/orders/sales-orders/amazon", "/orders/sales-orders/amazon/all"],
  },
  [amazonDfRouteId]: {
    urlPath: ["/orders/sales-orders/amazon/amazon-df"],
  },
  [amazonPoRouteId]: {
    urlPath: ["/orders/sales-orders/amazon/amazon-po"],
  },
  [amazonCanadaRouteId]: {
    urlPath: ["/orders/sales-orders/amazon/amazon-canada"],
  },
  [amazonSellerRouteId]: {
    urlPath: ["/orders/sales-orders/amazon/amazon-seller-central"],
  },
  [usFoodsRouteId]: {
    urlPath: [
      "/orders/sales-orders/us-foods",
      "/orders/sales-orders/us-foods/all",
    ],
  },
  [usFoodsUsRouteId]: {
    urlPath: ["/orders/sales-orders/us-foods/us"],
  },
  [usFoodsCsRouteId]: {
    urlPath: ["/orders/sales-orders/us-foods/us-foods-cs"],
  },
  [usFoodsDirectRouteId]: {
    urlPath: ["/orders/sales-orders/us-foods/us-foods-direct"],
  },
  [gfsRouteId]: {
    urlPath: ["/orders/sales-orders/gfs", "/orders/sales-orders/gfs/all"],
  },
  [gfsUsRouteId]: {
    urlPath: ["/orders/sales-orders/gfs/gfs-us"],
  },
  [gfsCanadaRouteId]: {
    urlPath: ["/orders/sales-orders/gfs/gfs-canada"],
  },
  [bradyRouteId]: {
    urlPath: ["/orders/sales-orders/brady"],
  },
  [baldorRouteId]: {
    urlPath: ["/orders/sales-orders/baldor"],
  },
  [baldorDfRouteId]: {
    urlPath: ["/orders/sales-orders/baldor/baldor-df"],
  },
  [baldorWarehouseRouteId]: {
    urlPath: ["/orders/sales-orders/baldor/baldor-warehouse"],
  },
  [cwStockRouteId]: {
    urlPath: ["/orders/sales-orders/chefswarehouse/cw-stock"],
  },
  [cwDirectRouteId]: {
    urlPath: ["/orders/sales-orders/chefswarehouse/cw-direct"],
  },
  [cwDropshipRouteId]: {
    urlPath: ["/orders/sales-orders/chefswarehouse/cw-dropship"],
  },
  [chefsWarehouseRouteId]: {
    urlPath: ["/orders/sales-orders/chefswarehouse"],
  },
  [cwRouteId]: {
    urlPath: ["/orders/sales-orders/chefswarehouse/cw"],
  },
  // Nested under Sysco in menuConfig.ts (`/orders/sales-orders/sysco/sotf`)
  // — this was registered as the bare `/orders/sales-orders/sotf`, which
  // never matches the real URL. SOTF itself still showed active (the
  // `findExact` fallback in useActiveMenu.ts matches on the menu item's
  // own `route` field, not this registry), but `isChildActive` walks
  // THIS registry to decide whether "Sysco" should show as a highlighted
  // parent, so the mismatch silently broke Sysco's parent highlighting
  // whenever SOTF was the active leaf.
  [sotfRouteId]: {
    urlPath: ["/orders/sales-orders/sysco/sotf"],
  },
  [singerEquipmentRouteId]: {
    urlPath: ["/orders/sales-orders/singer-equipment"],
  },
  [storeFrontRouteId]: {
    urlPath: ["/orders/sales-orders/store-front"],
  },
  [gfsStoresRouteId]: {
    urlPath: ["/orders/sales-orders/gfs/gfs-stores"],
  },
  [adminRouteId]: {
    urlPath: ["/orders/sales-orders/admin"],
  },
  [syscoRouteId]: {
    urlPath: ["/orders/sales-orders/sysco"],
  },
  [syscoDirectRouteId]: {
    urlPath: ["/orders/sales-orders/sysco/sysco-direct"],
  },
  [europeanImportsRouteId]: {
    urlPath: ["/orders/sales-orders/sysco/european-imports"],
  },
  [produceProRouteId]: {
    urlPath: ["/orders/sales-orders/produce-pro"],
  },
  [taoGroupRouteId]: {
    urlPath: ["/orders/sales-orders/tao-group"],
  },
  [legendsRouteId]: {
    urlPath: ["/orders/sales-orders/legends"],
  },
  [fourSeasonsRouteId]: {
    urlPath: ["/orders/sales-orders/four-seasons"],
  },
  [sodexoRouteId]: {
    urlPath: ["/orders/sales-orders/sodexo"],
  },
  // Had no registry entry at all — direct siblings of Admin/Sodexo/etc.
  // under "Sales orders", missing entirely rather than just mismatched.
  // Same symptom as the SOTF/ordersRouteId fixes above: the leaf itself
  // still showed active via the `findExact` fallback, but "Sales orders"
  // never got parent-highlighted while Northwood or Zoro was the active
  // leaf, since `isChildActive` found no pattern to match against.
  [northwoodRouteId]: {
    urlPath: ["/orders/sales-orders/northwood"],
  },
  [zoroRouteId]: {
    urlPath: ["/orders/sales-orders/zoro"],
  },
  [containerRouteId]: {
    urlPath: [
      "/purchasing/container",
      "/purchasing/container/NEW_CONTAINER?container-type=",
      "/purchasing/container?active=action-required",
      "/purchasing/container?active=containers",
    ],
  },
  [shipmentRouteId]: {
    urlPath: ["/purchasing/shipment"],
  },
  [poRouteId]: {
    urlPath: [
      "/purchasing/po",
      "/purchasing/purchase-order?active=my-po",
      "/purchasing/purchase-order?active=hold",
      "/purchasing/purchase-order?active=all",
      "/purchasing/purchase-order?active=vendor-review",
      "/purchasing/purchase-order/vendor/po/NEW_PO",
      "/purchasing/purchase-order/vendor/po",
    ],
  },
  [jobsRouteId]: {
    urlPath: ["/jobs"],
  },
  [ideationProduct]: {
    urlPath: [
      "/product/ideation",
      "/product/ideation/family/NEW_FAMILY/batch/NEW_BATCH",
      "/product/ideation/family/batch/NEW_BATCH",
      "/products/ideation/pricing/family/batch",
      "/product/ideation/family/batch",
    ],
  },
  [enrichProduct]: {
    urlPath: [
      "/product/enrichment",
      "/product/enrichment/family",
      "/products/enrichment/pricing/family",
      "/product/enrichment-batches/family/batch",
    ],
  },
  [incomingInventoryRouteId]: {
    urlPath: ["/inventory-report/incoming-inventory"],
  },
  [archivedInventoryRouteId]: {
    urlPath: ["/inventory-report/archived-inventory"],
  },
  [outOfStockRouteId]: {
    urlPath: [
      "/inventory-report/stock-report",
      "/inventory-report/out-stock-report",
    ],
  },
  [inStockRouteId]: {
    urlPath: ["/inventory-report/in-stock-report"],
  },
  [ediDashboardRouteId]: {
    urlPath: ["/dashboard/edi"],
  },
  [vendorsRouteId]: {
    urlPath: [
      "/purchasing/vendors",
      "/purchasing/vendors/approved",
      "/purchasing/vendors/pending-approval",
      "/purchasing/vendors/inactive",
      "/purchasing/vendor/NEW_VENDOR",
      "/purchasing/vendor",
      "/purchasing/vendor-bank",
    ],
  },
  [allProductsInventoryRouteId]: {
    urlPath: ["/inventory-report/all-stocks-report"],
  },
  [productExportRouteId]: {
    urlPath: ["/products/export"],
  },
  [salesExportRouteId]: {
    urlPath: ["/orders/report-export"],
  },
  [arContactsRouteId]: {
    urlPath: ["/accounts-receivable/contacts"],
  },
  // Dashboard > KPI. Mirrors RW-ERP-Service route.ts kpiDashboardRouteId
  // (`[DEFAULT_DASHBOARD_URL, '/dashboard/dashboards', '/dashboard/reports']`,
  // DEFAULT_DASHBOARD_URL === '/dashboard').
  [kpiDashboardRouteId]: {
    urlPath: ["/dashboard", "/dashboard/dashboards", "/dashboard/reports"],
  },
  // Orders > Pending Catalogs. Mirrors Vue route.ts pendingCatalogsRouteId.
  [pendingCatalogsRouteId]: {
    urlPath: ["/orders/pending-catalogs"],
  },
  // Orders > Shipping Prices. Vue route.ts maps only '/orders/shipping-prices',
  // but this page is a React-native route (`isReactRoute: true`,
  // APP_ROUTES.shippingPrices === '/shipping-prices'), so the React browser
  // path is added too — otherwise the leaf never matches in this app.
  [shippingPricesRouteId]: {
    urlPath: ["/orders/shipping-prices"],
  },
  // Products > Category & Collection. Mirrors Vue route.ts categoryAndCollectionRouteId.
  [categoryAndCollectionRouteId]: {
    urlPath: [
      "/product/category-and-collection",
      "/product/category-and-collection/NEW",
    ],
  },
};
